import { Money } from "@redotech/money";
import { useHandler } from "@redotech/react-util/hook";
import { Lazy } from "@redotech/react-util/init";
import {
  fullCoverage,
  getHtml,
  getReturnType,
  texts,
} from "@redotech/redo-model/checkbox";
import { Coverage, PaymentModel } from "@redotech/redo-model/coverage";
import { InfoModalVersion, ToggleFields } from "@redotech/redo-model/team";
import { InfoModal } from "@redotech/redo-model/widget";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { CURRENCY_FORMAT } from "@redotech/redo-web/currency";
import htmlReactParser from "html-react-parser";
import type { CSSProperties } from "react";
import {
  ChangeEvent,
  ReactNode,
  Suspense,
  lazy,
  memo,
  useMemo,
  useState,
} from "react";
import * as checkbox from "./checkbox.module.css";
import Info from "./info.svg";
import * as toggle from "./toggle.module.css";

const RedoModal = lazy(async () => {
  const { RedoModal } = await import("./modal");
  return { default: RedoModal };
});

const SingleToggleLabel = ({
  children,
  color,
  fontSize,
  coverage,
  price,
  onInfoClick,
  showInfoIcon,
  toggleText,
  toggleTextAfterRedo,
}: {
  children: ReactNode;
  color: string;
  fontSize?: number;
  coverage: Coverage;
  price?: string;
  onInfoClick: () => void;
  showInfoIcon?: boolean;
  toggleText?: string | null;
  toggleTextAfterRedo?: string | null;
}) => {
  const name =
    texts[
      `${+coverage.exchange}${+coverage.refund}${+coverage.storeCredit}` as `${0 | 1}${0 | 1}${0 | 1}`
    ];

  // toggleText may come from the singleToggleFields.titleText instead of autocheck.checkboxText
  // which means it may contain the %price% placeholder we will need to replace
  const toggleTextContainedPricePlaceholder =
    !!toggleText && toggleText.includes("%price%");

  return (
    <span
      className={toggle.label}
      data-target="toggle-label"
      style={{
        color,
        fontSize: fontSize ? `${fontSize.toString()}px` : "12px",
      }}
    >
      {toggleText ? (
        htmlReactParser(
          toggleTextContainedPricePlaceholder
            ? toggleText.replace(/%price%/g, price ?? "")
            : toggleText,
        )
      ) : (
        <>
          {children} {name !== fullCoverage && <> for {name} </>}
        </>
      )}
      {price && !toggleTextContainedPricePlaceholder && ` for ${price}`}{" "}
      {showInfoIcon && (
        <>
          <span className={toggle.modalTriggerSingle} data-target="toggle-info">
            <button
              className={checkbox.info}
              data-target="toggle-info-button"
              onClick={onInfoClick}
              type="button"
            >
              <Info className={checkbox.infoIcon} />
            </button>
          </span>{" "}
        </>
      )}
      {toggleTextAfterRedo}
    </span>
  );
};

export const TwoLineToggleLabel = ({
  children,
  color,
  fontSize,
  coverage,
  price,
  onInfoClick,
  showInfoIcon,
  toggleTitleText,
  toggleSubtitleText,
  titleFontWeight,
  badge,
  subtlePrice,
}: {
  children: ReactNode;
  color: string;
  fontSize?: number;
  coverage: Coverage;
  price?: string;
  onInfoClick: () => void;
  showInfoIcon?: boolean;
  toggleTitleText: string;
  toggleSubtitleText: string;
  titleFontWeight?: number;
  badge?: string;
  subtlePrice?: boolean;
}) => {
  const name =
    texts[
      `${+coverage.exchange}${+coverage.refund}${+coverage.storeCredit}` as `${0 | 1}${0 | 1}${0 | 1}`
    ];

  const titleTextContainedPricePlaceholder =
    !!toggleTitleText && toggleTitleText.includes("%price%");

  return (
    <div className={toggle.doubleTextContainer}>
      {/* This logic is temporarily here for Lime Ricki's A/B test, it will be removed in the near future. */}
      {toggleTitleText !== "%%HIDEME%%" && (
        <span
          className={toggle.label}
          data-target="toggle-label"
          style={{
            color,
            fontSize: fontSize ? `${fontSize.toString()}px` : "12px",
            fontWeight: titleFontWeight,
          }}
        >
          {toggleTitleText ? (
            htmlReactParser(
              titleTextContainedPricePlaceholder
                ? toggleTitleText.replace(/%price%/g, price ?? "")
                : toggleTitleText,
            )
          ) : (
            <>
              {children} {name !== fullCoverage && <>for {name} </>}
            </>
          )}
          {price && !titleTextContainedPricePlaceholder ? (
            subtlePrice ? (
              <span data-target="price-value">{` ${price}`}</span>
            ) : (
              ` for ${price}`
            )
          ) : null}
          {badge && (
            <div className={toggle.badge}>
              <RedoBadge color="blue" text={badge} />
            </div>
          )}
          {showInfoIcon && (
            <span
              className={toggle.modalTriggerDouble}
              data-target="toggle-info"
            >
              <button
                className={checkbox.info}
                data-target="toggle-info-button"
                onClick={onInfoClick}
                type="button"
              >
                <Info className={checkbox.infoIcon} />
              </button>
            </span>
          )}
        </span>
      )}
      <br />
      <span
        className={toggle.subtext}
        data-target="toggle-subtext"
        style={{ color }}
      >
        {htmlReactParser(toggleSubtitleText)}
      </span>
    </div>
  );
};

const Toggle = memo(function Toggle({
  color,
  value,
  onValue,
  toggleOnColor,
  toggleOffColor,
  toggleCircleColor,
}: {
  color: string;
  value: boolean;
  onValue?: (value: boolean) => void;
  toggleOnColor?: string;
  toggleOffColor?: string;
  toggleCircleColor?: string;
}) {
  const onChange = useMemo(
    () => (e: ChangeEvent) => {
      onValue?.((e.target as HTMLInputElement).checked);
    },
    [onValue],
  );

  const memoizedComponent = useMemo(
    () => (
      <div style={{ "--redo-color": color } as CSSProperties}>
        <label
          className={toggle.toggleContainer}
          data-target="toggle-container"
        >
          <input
            checked={value}
            className={toggle.checkbox}
            data-target="toggle-input"
            onChange={onChange}
            type="checkbox"
          />
          <div
            className={toggle.switch0}
            data-target="toggle-switch"
            style={{ backgroundColor: value ? toggleOnColor : toggleOffColor }}
          >
            <div
              className={toggle.circle}
              data-target="toggle-circle"
              style={{
                backgroundColor: toggleCircleColor,
                left: value ? "18px" : "2px",
              }}
            />
          </div>
        </label>
      </div>
    ),
    [color, value, onChange, toggleOnColor, toggleOffColor, toggleCircleColor],
  );

  return memoizedComponent;
});

export const ToggleImage = memo(function ToggleImage({
  img_url,
}: {
  img_url: string;
}) {
  return (
    <img
      className={toggle.imageContainer}
      data-target="deluxe-toggle-image"
      src={img_url}
    />
  );
});

export const RedoToggle = ({
  color,
  coverage,
  html,
  fontSize,
  toggleText,
  toggleTextAfterRedo,
  toggleFields,
  value,
  onChange,
  paymentModel,
  price,
  isDelux,
  customCss,
  toggleOnColor,
  toggleOffColor,
  toggleCircleColor,
  packageProtectionPlusEnabled,
  packageProtectionEnabled,
  showInfoIcon,
  modalVersion,
  modalCustomizationsV2,
}: {
  color: string;
  coverage: Coverage;
  html?: string | null;
  fontSize?: number;
  toggleText?: string | null;
  toggleTextAfterRedo?: string | null;
  toggleFields: ToggleFields;
  value: boolean;
  onChange?(value: boolean): void;
  paymentModel: PaymentModel;
  price: Money | undefined;
  isDelux: boolean;
  customCss?: string;
  toggleOnColor?: string;
  toggleOffColor?: string;
  toggleCircleColor?: string;
  packageProtectionPlusEnabled?: boolean;
  packageProtectionEnabled?: boolean;
  showInfoIcon?: boolean;
  modalVersion?: InfoModalVersion;
  modalCustomizationsV2?: InfoModal;
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  // const handleAdd = useHandler(() => {
  //   onChange && onChange(true);
  // }); // TODO(paul): would be nice to add to cart

  const onInfoClick = useHandler(() => {
    setModalOpen(true);
  });

  const onModalClose = useHandler(() => setModalOpen(false));

  const label = isDelux ? (
    <TwoLineToggleLabel
      color={color}
      coverage={coverage}
      fontSize={fontSize}
      onInfoClick={onInfoClick}
      price={
        paymentModel === "customer_paid" && price
          ? CURRENCY_FORMAT(price.currency).format(price.amount)
          : undefined
      }
      showInfoIcon={showInfoIcon}
      toggleSubtitleText={
        value ? toggleFields.positiveSubtext : toggleFields.negativeSubtext
      }
      toggleTitleText={toggleFields.titleText}
    >
      {html ? (
        htmlReactParser(getHtml(html, getReturnType(coverage)) ?? "")
      ) : (
        <strong>Free Unlimited Returns</strong>
      )}
    </TwoLineToggleLabel>
  ) : (
    <SingleToggleLabel
      color={color}
      coverage={coverage}
      fontSize={fontSize}
      onInfoClick={onInfoClick}
      price={
        paymentModel === "customer_paid" && price
          ? CURRENCY_FORMAT(price.currency).format(price.amount)
          : undefined
      }
      showInfoIcon={showInfoIcon}
      toggleText={toggleText}
      toggleTextAfterRedo={toggleTextAfterRedo}
    >
      {html ? (
        htmlReactParser(getHtml(html, getReturnType(coverage)) ?? "")
      ) : (
        <strong>Free Unlimited Returns</strong>
      )}
    </SingleToggleLabel>
  );

  let checkbox_: ReactNode;
  switch (paymentModel) {
    case PaymentModel.CUSTOMER_PAID:
      checkbox_ = (
        <Toggle
          color={color}
          onValue={onChange}
          toggleCircleColor={toggleCircleColor}
          toggleOffColor={toggleOffColor}
          toggleOnColor={toggleOnColor}
          value={value}
        />
      );
      break;
    case PaymentModel.MERCHANT_PAID:
      checkbox_ = <span />;
      break;
  }

  return (
    <>
      {customCss ? <style>{customCss}</style> : null}
      <div
        className={toggle.container}
        data-target="toggle-root"
        style={{ color }}
      >
        {label}
        {checkbox_}
      </div>
      <Suspense>
        <Lazy init={modalOpen}>
          <RedoModal
            enableTopLayerModalApi={
              modalCustomizationsV2?.enableTopLayerModalApi
            }
            modalCustomizationsV2={modalCustomizationsV2}
            onClose={onModalClose}
            open={modalOpen}
            packageProtectionEnabled={packageProtectionEnabled}
            packageProtectionPlusEnabled={packageProtectionPlusEnabled}
            returnsCoverage={coverage}
            version={modalVersion}
          />
        </Lazy>
      </Suspense>
    </>
  );
};

export const DeluxeToggleArray = ({
  image_url,
  children,
}: {
  image_url: string;
  children: ReactNode;
}) => {
  return (
    <div className={toggle.deluxeContainerAll} data-target="deluxe-toggle-root">
      <ToggleImage img_url={image_url} />
      <div
        className={toggle.deluxeContainerTextToggle}
        data-target="deluxe-toggle-container"
      >
        {children}
      </div>
    </div>
  );
};
